import React, { Fragment, SyntheticEvent } from 'react'
import { formattedDate } from 'utils/formatters'
import Income from 'components/Income'
import StarRatings from 'components/StarRatings'
import { camelCase } from 'lodash'
import Icon from 'components/Icon'

import BusinessSource from 'components/BusinessSource'
import TitledContent from 'components/TitledContent'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import styles from './BusinessActivity.scss'
import messages from './messages'

export const titledFields = ['valuation', 'projectPriceAsString', 'totalCharges', 'cost']

export interface IReview {
  userName: string
  userLocation: string
  userPhoto: string
  reviewSource: string
  reviewRating: string
  reviewText: string
  projectStartDate: string
  projectEndDate: string
  eventDate: string
  cost: number
  permitNo: string
  reviewTitle: string
  costMax: number
  reviewPhoto: string
}

export interface IBusinessActivity {
  review: IReview
}

const BusinessActivity = ({ review, intl }: IBusinessActivity & InjectedIntlProps) => {
  const {
    userName,
    userLocation,
    userPhoto,
    reviewSource,
    reviewRating,
    reviewText,
    projectStartDate,
    projectEndDate,
    eventDate,
    cost,
    permitNo,
    reviewTitle,
    costMax,
    reviewPhoto,
  } = review

  const photo = userPhoto || require('./assets/avatar.png')

  const hasPrice = titledFields.filter(key => !!review[key as keyof IReview]).length > 0

  const onError = (ev: SyntheticEvent<HTMLImageElement>) => {
    ;(ev.target as HTMLInputElement).src = require('./assets/avatar.png')
    return true
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.reviewer}>
          {permitNo && <span className={styles.name}>{permitNo}</span>}
          {!permitNo && (
            <span>
              <img src={photo} className={styles.avatar} onError={onError} alt='' />
              <span className={styles.name}>{userName || 'Anonymous'}</span>
            </span>
          )}
          <div className={styles.details}>
            <div className={styles.location}>{userLocation}</div>
            <div className={styles.activityDate}>
              {eventDate && !projectStartDate && (
                <section className={styles.date}>{formattedDate(eventDate, 'MMM DD, YYYY')}</section>
              )}
              {projectStartDate && projectEndDate && (
                <section className={styles.date}>
                  {formattedDate(projectStartDate, 'MMM DD, YYYY')} - {formattedDate(projectEndDate, 'MMM DD, YYYY')}
                </section>
              )}
              {projectStartDate && !projectEndDate && (
                <section className={styles.date}>{formattedDate(projectStartDate, 'MMM DD, YYYY')} - Present</section>
              )}
              <span className={styles.source}>
                <BusinessSource source={camelCase(reviewSource)} isDisabled />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.review}>
        <div className={styles.metadata}>
          {hasPrice ? <Icon name='coin' size='regular' /> : <Icon name='review' size='regular' />}
          {reviewTitle && <span className={styles.reviewTitle}>{reviewTitle}</span>}
          <span className={styles.rating}>{reviewRating && <StarRatings rating={reviewRating} />}</span>
        </div>
        <div className={styles.content}>
          {reviewPhoto && <img src={reviewPhoto} alt='' className={styles.reviewPhoto} />}
          {reviewText}
        </div>
      </div>
      <div className={styles.value}>
        {titledFields
          .filter(key => !!review[key as keyof IReview])
          .map(key => (
            <TitledContent
              key={key}
              title={intl.formatMessage(messages[key as keyof typeof messages])}
              className={styles.field}
              titleClassName={styles.fieldTitle}
              contentClassName={styles.fieldContent}
            >
              {key !== 'cost' || !costMax ? (
                <Income value={review[key as keyof IReview]} />
              ) : (
                <Fragment key={key}>
                  {costMax !== -1 ? (
                    <span>
                      <Income value={review[key]} />-<Income value={costMax} />
                    </span>
                  ) : (
                    <span>
                      More than <Income value={review[key]} />
                    </span>
                  )}
                </Fragment>
              )}
            </TitledContent>
          ))}
      </div>
    </div>
  )
}

export default injectIntl<IBusinessActivity>(BusinessActivity)
